import { create } from 'zustand';
import { clientFavoritesControllerApi } from '@api/api-config.ts';
import { useToastStore } from '@stores/toastStore';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import i18next from 'i18next';

type FavouritesStore = {
  loading: boolean;
  storingId: number | null;

  error: string | null;

  favouritesIds: number[]; // Array of favourite item IDs

  addToFavourites: (itemId: number, productName?: string) => Promise<void>;
  removeFromFavourites: (itemId: number, productName?: string) => Promise<void>;
  loadFavourites: (searchQuery?: string) => Promise<void>;
};

export const useFavouritesStore = create<FavouritesStore>(set => ({
  loading: false,
  favouritesIds: [],
  error: null,
  storingId: null,

  loadFavourites: async () => {
    try {
      set({ loading: true, error: null });
      const favourites = await loadFavourites();
      set({ loading: false, favouritesIds: favourites.data.map(t => t) });
    } catch (error) {
      console.error('Error loading favourites:', error);
      set({ loading: false, error: 'Failed to load favourites' });
    }
  },
  addToFavourites: async (itemId: number, productName?: string) => {
    try {
      set({ storingId: itemId, error: null });
      const { orgId: organizationId, portalId } = useOrgPortal.getState();
      const result = await clientFavoritesControllerApi.addProductToFavorites({ productId: itemId, organizationId, portalId });

      set({ storingId: null, favouritesIds: result.data });

      useToastStore.getState().addToast({
        severity: 'success',
        summary: i18next.t('client:favorites.addedToFavorites'),
        detail: productName
          ? i18next.t('client:favorites.addedToFavoritesDetail', { name: productName })
          : i18next.t('client:favorites.addedToFavorites'),
      });
    } catch (error) {
      console.error('Error adding to favourites:', error);
      set({ storingId: null, error: 'Failed to add to favourites' });

      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail: i18next.t('client:favorites.addedToFavorites'),
      });
    }
  },

  removeFromFavourites: async (itemId: number, productName?: string) => {
    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();
      const result = await clientFavoritesControllerApi.removeFromFavorites({ productId: itemId, organizationId, portalId });
      set({ storingId: null, favouritesIds: result.data });

      useToastStore.getState().addToast({
        severity: 'success',
        summary: i18next.t('client:favorites.removedFromFavorites'),
        detail: productName
          ? i18next.t('client:favorites.removedFromFavoritesDetail', { name: productName })
          : i18next.t('client:favorites.removedFromFavorites'),
      });
    } catch (error) {
      console.error('Error removing from favourites:', error);
      set({ storingId: null, error: 'Failed to remove from favourites' });

      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail: i18next.t('client:favorites.removedFromFavorites'),
      });
    }
  },
}));

const loadFavourites = async () => {
  try {
    const { orgId: organizationId, portalId } = useOrgPortal.getState();
    return clientFavoritesControllerApi.getFavoriteIds({ portalId, organizationId });
  } catch (error) {
    throw error;
  }
};
